import { useQuery } from '@tanstack/react-query'
import type { MeteringCallOutputBodyReadable } from '@/shared/api/__generated__'
import { useObjectUrl } from '@/shared/lib'
import { type CallReport, parseCallReport } from '../model/report'
import { type CallAnalysis, emptyAnalysis, parseCallTelemetry } from '../model/telemetry'
import { getCallOptions, getCallRecordingOptions, getCallTelemetryOptions } from './queries'

export interface CallQueryState {
  isPending: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export interface CallDetails extends CallQueryState {
  call: MeteringCallOutputBodyReadable | undefined
  report: CallReport
}

// Шапка звонка плюс сырой отчёт call.result: свёртка телеметрии, факты и
// статус записи разбора. Типизация отчёта живёт в парсере — экран её не знает.
export function useCall(callId: string): CallDetails {
  const query = useQuery(getCallOptions({ path: { call_id: callId } }))
  return {
    call: query.data,
    report: parseCallReport(query.data?.report),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

// Разбор ход за ходом едет своей ручкой: он вправе не доехать вовсе, и его
// отсутствие не должно ронять карточку — поэтому запрос отдельный и без ретрая.
export function useCallTelemetry(callId: string): CallQueryState & { analysis: CallAnalysis } {
  const query = useQuery({
    ...getCallTelemetryOptions({ path: { call_id: callId } }),
    retry: false,
  })
  return {
    analysis: query.data ? parseCallTelemetry(query.data) : emptyAnalysis(callId),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

// Ручка записи закрыта Bearer'ом — `<audio src>` заголовок не пошлёт, поэтому
// запись качается SDK-вызовом в blob и живёт object-URL'ом до размонтирования.
// Тянется только по требованию: разговор весит мегабайты, а нужен не всегда.
export function useCallRecording(
  callId: string,
  enabled: boolean,
): CallQueryState & { url: string | null } {
  const query = useQuery({
    ...getCallRecordingOptions({ path: { call_id: callId }, parseAs: 'blob' }),
    enabled,
    // Запись завершённого звонка неизменяема: перекачивать её на фокус окна незачем.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })
  const blob = query.data instanceof Blob ? query.data : null

  return {
    url: useObjectUrl(blob),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}
